import UpgradeDialog from '../upgrade/UpgradeDialog';
import { useUpgradeExperience } from '../upgrade/UpgradeExperience';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ref } from 'firebase/database';
import { db } from '../../firebaseConfig';
import ActiveGoalsPanel from '../components/ActiveGoalsPanel';
import MobileSummary from '../components/MobileSummary';
import { getCareerFocus } from '../careerCoachApi';
import { getAndCacheFirebaseValue, getCachedFirebaseValue } from '../firebaseDataCache';
import { useLanguage } from '../contexts/LanguageContext';

const NODE_SIZE = 78;
const ICON_SIZE = 27;

const COLORS = {
  blue: '#568DED',
  green: '#40EB6E',
  purple: '#CC40EB',
  grey: '#808080',
  yellow: '#FFD700',
  bg: '#031128',
};

const toRgba = (hex, opacity) => {
  const clean = hex.replace('#', '');
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

const COACH_NODES = [
  { key: 'interviews', icon: 'fa-solid fa-comments', color: COLORS.green, lightTone: 'green' },
  { key: 'ask',        icon: 'fa-solid fa-message', color: COLORS.blue, lightTone: 'blue' },
  { key: 'scout',      icon: 'fa-solid fa-screwdriver-wrench', color: COLORS.grey, lightTone: 'neutral' },
  { key: 'careers',    icon: 'fa-solid fa-compass', color: COLORS.purple, lightTone: 'purple' },
];

const SECONDARY_CONTROL_OPACITY = 0.12;

const NODE_COMING_SOON = {
  interviews: 'Practice Interviews — coming soon',
};

const DEFAULT_TARGET_TITLES = [
  'AI Engineer (95% fit)',
  'AI Researcher (90% fit)',
];

const DEFAULT_TARGET_TITLES_AR = [
  'مهندس ذكاء اصطناعي (ملاءمة ٩٥٪)',
  'باحث ذكاء اصطناعي (ملاءمة ٩٠٪)',
];

function localizedList(items, language, type = 'disjunction') {
  return new Intl.ListFormat(language === 'ar' ? 'ar-LB' : 'en-US', { style: 'long', type }).format(items);
}

function localizedNumber(value, language) {
  return new Intl.NumberFormat(language === 'ar' ? 'ar-LB' : 'en-US').format(value);
}

// Derive a human-readable focus statement from the scout document.
// No backend endpoint yet — client-side only from the saved scout data.
export function buildFocusStatement(profile, scout, language = 'en') {
  const titles      = scout?.goals?.targetTitles            || [];
  const jobTypes    = scout?.goals?.jobTypes                || [];
  const remotePrefs = scout?.locationPreferences?.remotePreference || [];
  const geoScopes   = scout?.locationPreferences?.geoScopes || [];
  const cities      = scout?.locationPreferences?.cities    || [];
  const countries   = scout?.locationPreferences?.countries || [];
  const industries  = scout?.preferences?.industriesPreferred || [];
  const salary      = scout?.preferences?.salary            || {};
  const careerGoals = scout?.goals?.careerGoals             || [];

  const isContractor = jobTypes.some((t) => t === 'contractor' || t === 'contract');
  const isFullTime   = jobTypes.some((t) => t === 'full_time');
  const parts = [];
  const isArabic = language === 'ar';

  if (titles.length > 0) {
    const typeLabel = isArabic
      ? (isContractor ? 'فرص تعاقد' : isFullTime ? 'وظائف بدوام كامل' : 'فرص')
      : (isContractor ? 'contractor opportunities' : isFullTime ? 'full-time roles' : 'opportunities');
    const titleList = localizedList(titles.slice(0, 3), language);
    const places = [...cities.slice(0, 2), ...countries.slice(0, 1), ...geoScopes.slice(0, 1)].filter(Boolean);
    const hasRemote = remotePrefs.some((r) => r === 'remote' || r === 'remote_only');
    if (isArabic) {
      const locationPart = hasRemote && places.length > 0
        ? `عن بُعد أو في ${places[0]}`
        : hasRemote ? 'عن بُعد'
        : places.length > 0 ? `في ${localizedList(places.slice(0, 2), language)}`
        : '';
      parts.push(`أبحث الآن عن ${typeLabel} بصفة ${titleList}${locationPart ? ` ${locationPart}` : ''}.`);
    } else {
      const locationPart = hasRemote && places.length > 0
        ? `Remote or across ${places[0]}`
        : hasRemote ? 'Remote'
        : places.length > 0 ? localizedList(places.slice(0, 2), language)
        : '';
      parts.push(`Right now, I am looking for ${typeLabel} as ${titleList}${locationPart ? ` in ${locationPart}` : ''}.`);
    }
  } else if (profile?.targetRole) {
    parts.push(isArabic
      ? `أركّز الآن على إيجاد وظائف بصفة ${profile.targetRole}.`
      : `Right now, I am focused on finding roles as ${profile.targetRole}.`);
  }

  if (salary.minRate || salary.maxRate) {
    const unit = salary.unit || 'hourly';
    if (isArabic) {
      const unitLabel = unit === 'hourly' ? 'بالساعة' : unit === 'monthly' ? 'شهري' : unit === 'yearly' || unit === 'annual' ? 'سنوي' : unit;
      if (salary.minRate && salary.maxRate) {
        parts.push(`أبحث عن أجر ${unitLabel} يتراوح بين ${localizedNumber(salary.minRate, language)} و${localizedNumber(salary.maxRate, language)} دولاراً.`);
      } else if (salary.minRate) {
        parts.push(`أبحث عن أجر ${unitLabel} يبدأ من ${localizedNumber(salary.minRate, language)} دولاراً.`);
      }
    } else if (salary.minRate && salary.maxRate) {
      parts.push(`I am looking for ${unit} compensation between $${salary.minRate} and $${salary.maxRate}.`);
    } else if (salary.minRate) {
      parts.push(`I am looking for ${unit} compensation starting at $${salary.minRate}.`);
    }
  }

  if (remotePrefs.length > 0) {
    const labels = remotePrefs.map((r) => {
      if (r === 'remote' || r === 'remote_only') return isArabic ? 'عن بُعد' : 'Remote';
      if (r === 'hybrid') return isArabic ? 'هجين' : 'Hybrid';
      if (r === 'onsite' || r === 'on_site') return isArabic ? 'من مقر العمل' : 'On-site';
      return null;
    }).filter(Boolean);
    if (labels.length > 0) parts.push(isArabic
      ? `أنا منفتح على وظائف ${localizedList(labels, language, 'conjunction')}.`
      : `I am open for ${localizedList(labels, language, 'conjunction')} roles.`);
  }

  if (industries.length > 0) {
    parts.push(isArabic
      ? `تشمل القطاعات التي أستهدفها ${localizedList(industries.slice(0, 4), language, 'conjunction')}.`
      : `My target industries include ${localizedList(industries.slice(0, 4), language, 'conjunction')}.`);
  }

  if (careerGoals.length > 0 && parts.length < 2) {
    parts.push(isArabic ? `هدفي هو ${careerGoals[0]}.` : `My goal is to ${careerGoals[0].toLowerCase()}.`);
  }

  if (parts.length === 0) {
    return isArabic
      ? 'أبحث الآن عن فرص تناسب خبرتي وتفضيلاتي وخطوتي المهنية التالية.'
      : 'Right now, I am looking for opportunities that fit my experience, preferences, and next career move.';
  }

  return parts.join(' ');
}

function CoachNode({ node, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={node.title.replace('\n', ' ')}
      className={`js-coach-node${active ? ' is-active' : ''}`}
      style={{
        '--coach-node-color': node.color,
        '--coach-node-light-color': `var(--js-accent-${node.lightTone})`,
        '--coach-node-light-bg': `var(--js-accent-${node.lightTone}-${active ? 'soft-strong' : 'soft'})`,
        '--coach-node-light-hover-bg': `var(--js-accent-${node.lightTone}-soft-strong)`,
        '--coach-node-light-ring': `var(--js-accent-${node.lightTone}-ring)`,
        '--coach-node-tint': toRgba(node.color, active ? 0.22 : SECONDARY_CONTROL_OPACITY),
        '--coach-node-hover-tint': toRgba(node.color, 0.22),
        '--coach-node-ring': toRgba(node.color, 0.28),
      }}
    >
      <span
        className="js-coach-node__circle"
        style={{
          width: NODE_SIZE,
          height: NODE_SIZE,
        }}
        aria-hidden="true"
      >
        <i
          className={node.icon}
          style={{
            fontSize: ICON_SIZE,
            width: ICON_SIZE,
            height: ICON_SIZE,
            lineHeight: `${ICON_SIZE}px`,
          }}
        />
      </span>
      <span className="js-coach-node__label">{node.title}</span>
    </button>
  );
}

function CareerCoachScreen({ user, profile }) {
  const location = useLocation();
  const navigate = useNavigate();
  const experience = useUpgradeExperience();
  const [scoutUpgrade, setScoutUpgrade] = useState(null);
  const editScout = (role = "") => {
    if (experience?.paid) navigate("/configure-scout", { state: { targetRole: role } });
    else setScoutUpgrade({ role });
  };
  const { language, t } = useLanguage();
  const tc = (key) => t(`careerCoachHub.${key}`);
  const scoutId = profile?.primaryScoutId || null;
  const scoutUpdateQueued = Boolean(location.state?.scoutUpdateQueued);
  const scoutPath = user?.uid && scoutId ? `people/${user.uid}/jobScouts/${scoutId}` : '';
  const coachPath = user?.uid ? `people/${user.uid}/careerCoach` : '';
  const cachedScout = scoutPath ? getCachedFirebaseValue(scoutPath) : undefined;
  const cachedCoach = coachPath ? getCachedFirebaseValue(coachPath) : undefined;
  const cachedSummary = cachedCoach?.focusSummary || '';
  const cachedSummaryMatchesScout = cachedSummary
    && cachedCoach?.focusSummaryScoutVersion === (cachedScout?.updatedAt || 0)
    && cachedCoach?.focusSummaryLocale === language;

  const [scout, setScout]                   = useState(cachedScout || null);
  const [scoutLoading, setScoutLoading]     = useState(Boolean(scoutPath) && cachedScout === undefined);
  const [focusSummary, setFocusSummary]     = useState(() => (
    cachedSummaryMatchesScout ? cachedSummary : (cachedScout !== undefined ? buildFocusStatement(profile, cachedScout, language) : '')
  ));
  const [focusLoading, setFocusLoading]     = useState(Boolean(scoutPath) && cachedScout === undefined);
  const [activeNode, setActiveNode]         = useState(null);

  // Load scout + careerCoach cache in parallel.
  // We read directly from RTDB rather than using the profile prop because the
  // optimistic post-onboarding state stores the key as the literal string
  // "jobScouts/{id}" (not nested), so profile.jobScouts[scoutId] is undefined
  // until the next full profile reload.
  useEffect(() => {
    if (!user?.uid || !scoutId) {
      setScout(null);
      setFocusSummary(tc('defaultFocus'));
      setScoutLoading(false);
      setFocusLoading(false);
      return;
    }

    let cancelled = false;

    if (cachedScout !== undefined) {
      setScout(cachedScout || null);
      setScoutLoading(false);
      const cachedSummary = cachedCoach?.focusSummary || '';
      const cachedVersion = cachedCoach?.focusSummaryScoutVersion || 0;
      const cachedLocale = cachedCoach?.focusSummaryLocale || 'en';
      if (cachedSummary && cachedVersion === (cachedScout?.updatedAt || 0) && cachedLocale === language) {
        setFocusSummary(cachedSummary);
      } else {
        setFocusSummary(buildFocusStatement(profile, cachedScout, language));
      }
      setFocusLoading(false);
    } else {
      setScoutLoading(true);
      setFocusLoading(true);
    }

    Promise.all([
      getAndCacheFirebaseValue(ref(db, scoutPath), scoutPath),
      getAndCacheFirebaseValue(ref(db, coachPath), coachPath),
    ])
      .then(([scoutSnap, coachSnap]) => {
        if (cancelled) return;
        const loadedScout = scoutSnap.exists() ? scoutSnap.val() : null;
        setScout(loadedScout);

        const coachData = coachSnap.exists() ? coachSnap.val() : {};
        const cachedSummary = coachData.focusSummary || '';
        const cachedVersion = coachData.focusSummaryScoutVersion || 0;
        const cachedLocale = coachData.focusSummaryLocale || 'en';
        const scoutVersion  = loadedScout?.updatedAt || 0;

        // Use the RTDB-cached summary if the scout hasn't changed since it was generated.
        // This avoids an API call on every page load.
        if (cachedSummary && cachedVersion === scoutVersion && cachedLocale === language) {
          setFocusSummary(cachedSummary);
          setFocusLoading(false);
          return;
        }

        // Scout changed (or no cache yet) — ask the backend to regenerate.
        const fallbackSummary = buildFocusStatement(profile, loadedScout, language);
        setFocusSummary(fallbackSummary); // show client fallback immediately
        getCareerFocus({ user })
          .then((res) => {
            if (!cancelled) setFocusSummary(res.summary || fallbackSummary);
          })
          .catch(() => {}) // keep the client-side fallback on error
          .finally(() => {
            if (!cancelled) setFocusLoading(false);
          });
      })
      .catch(() => {
        if (cancelled) return;
        setScout(null);
        setFocusSummary(tc('defaultFocus'));
        setScoutLoading(false);
        setFocusLoading(false);
      })
      .finally(() => {
        if (!cancelled) setScoutLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [user?.uid, scoutId, language]); // eslint-disable-line react-hooks/exhaustive-deps

  const targetTitles   = scout?.goals?.targetTitles?.length > 0
    ? scout.goals.targetTitles
    : language === 'ar' ? DEFAULT_TARGET_TITLES_AR : DEFAULT_TARGET_TITLES;
  const displaySummary = focusSummary || buildFocusStatement(profile, scout, language);
  const localizedNodes = COACH_NODES.map((node) => ({ ...node, title: tc(`nodes.${node.key}`) }));

  return (
    <div className="js-coach">
      <h2 className="js-coach__title">{tc('title')}</h2>

      {scoutUpdateQueued && (
        <div className="js-coach__update-banner" role="status">
          <i className="fa-solid fa-arrows-rotate" aria-hidden="true" />
          <p>{tc('update')}</p>
        </div>
      )}

      <div className="js-coach__nodes">
        {localizedNodes.map((node) => (
          <div key={node.key} className="js-coach__node-wrap">
            <CoachNode
              node={node}
              active={activeNode === node.key}
              onClick={() => {
                if (node.key === 'interviews') {
                  navigate('/mock-interview');
                  return;
                }
                if (node.key === 'ask') {
                  navigate('/ask-anything');
                  return;
                }
                if (node.key === 'scout') {
                  editScout();
                  return;
                }
                if (node.key === 'careers') {
                  navigate('/career-paths');
                  return;
                }
                setActiveNode(activeNode === node.key ? null : node.key);
              }}
            />
          </div>
        ))}
      </div>

      {activeNode && (
        <p className="js-coach__node-hint">
          <i className="fa-solid fa-clock-rotate-left" style={{ marginRight: 8, color: COLORS.blue }} />
          {NODE_COMING_SOON[activeNode]}
        </p>
      )}

      <div className="js-coach__content">
        {/* ── Left column ── */}
        <div className="js-coach__left">

          {/* Focus statement */}
          <div className="js-coach__focus-card">
            <div className="js-coach__focus-head">
              <i className="fa-solid fa-bullseye" style={{ color: COLORS.blue, fontSize: 18 }} />
              <h3>{tc('focusTitle')}</h3>
            </div>
            {scoutLoading ? (
              <div style={{ height: 60, display: 'grid', placeItems: 'center' }}>
                <div className="js-personnel-spinner" />
              </div>
            ) : (
              <MobileSummary className={`js-coach__focus-text ${focusLoading ? 'is-generating' : ''}`}>{`“${displaySummary}”`}</MobileSummary>
            )}
          </div>

          {/* Job Scout titles */}
          <div className="js-coach__scout-section">
            <h3 className="js-coach__scout-title">{tc('scoutTitle')}</h3>
            <p className="js-coach__scout-desc">
              {tc('scoutDescription')}
            </p>

            {scoutLoading ? (
              <div style={{ height: 38, display: 'grid', placeItems: 'center start' }}>
                <div className="js-personnel-spinner" />
              </div>
            ) : (
              <div className="js-coach__scout-pills">
                {targetTitles.length === 0 && (
                  <span className="js-coach__scout-empty">{tc('noTitles')}</span>
                )}
                {targetTitles.map((title) => (
                  <button key={title} type="button" className="js-coach__scout-pill" dir="auto">
                    {title}
                  </button>
                ))}
                <button
                  type="button"
                  className="js-coach__scout-pill js-coach__scout-pill--add"
                  onClick={() => editScout()}
                >
                  <i className="fa-solid fa-plus" style={{ fontSize: 12 }} /> {tc('addMore')}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* ── Right column: Active Goals ── */}
        <div className="js-coach__right">
          <ActiveGoalsPanel user={user} />
        </div>
      </div>
      {scoutUpgrade && !experience?.paid && <UpgradeDialog user={user} context="scout_edit" onClose={() => setScoutUpgrade(null)} destination={{ path: "/configure-scout" }} />}
    </div>
  );
}

export default CareerCoachScreen;
